// --- FREE RESPONSE (compact single‑line & multi‑answer) --------------------
import React, { useState, useEffect, useRef } from "react";
import InfoTooltip from "./InfoTooltip";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  TextField,
  Fade,
  Paper,
} from "@mui/material";
import {
  Edit as EditIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { isEqual } from "lodash";

// ---------------- helper utilities ----------------------------------------
const initializeAnswers = (val, multi) => {
  if (multi) {
    if (Array.isArray(val)) return val.filter((v) => v && v.trim());
    if (typeof val === "string" && val.trim()) return [val];
    return [];
  }
  if (Array.isArray(val) && val.length) return [val[0]];
  if (typeof val === "string") return [val];
  return [];
};

const truncateAnswer = (str, n = 120) =>
  str.length <= n ? str : `${str.slice(0, n)}……`;

// Determine if all answers are short enough for compact layout
const shouldUseCompactLayout = (answers, threshold = 40) => {
  if (!answers || answers.length === 0) return false;
  return answers.every(answer => answer.trim().length <= threshold);
};

// ---------------- main component ------------------------------------------
const FreeResponse = ({
  label,
  mandatory = false,
  onChange,
  instruction,
  readOnly = false,
  value = [],
  allowMultipleAnswers = false,
  showError = false,
}) => {
  const [answers, setAnswers] = useState(
    initializeAnswers(value, allowMultipleAnswers)
  );
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [dialogMode, setDialogMode] = useState("add");
  const [dialogError, setDialogError] = useState(false);
  const [hover, setHover] = useState({});
  const prevAnswersRef = useRef(answers);

  // sync with incoming props
  useEffect(() => {
    const newVal = initializeAnswers(value, allowMultipleAnswers);
    if (!isEqual(newVal, prevAnswersRef.current)) {
      setAnswers(newVal);
      prevAnswersRef.current = newVal;
    }
  }, [value, allowMultipleAnswers]);

  // mandatory validation
  useEffect(() => {
    const ok = answers.some((a) => a && a.trim());
    setError(mandatory && !ok);
  }, [answers, mandatory]);

  // propagate to parent
  useEffect(() => {
    if (!isEqual(answers, prevAnswersRef.current)) {
      const outgoing = allowMultipleAnswers ? answers : answers[0] ?? "";
      onChange?.(outgoing);
      prevAnswersRef.current = answers;
    }
  }, [answers, allowMultipleAnswers]);

  // handlers
  const openAdd = () => {
    setCurrentAnswer("");
    setEditIdx(null);
    setDialogMode("add");
    setDialogError(false);
    setDialogOpen(true);
  };
  const openEdit = (i) => {
    setCurrentAnswer(answers[i]);
    setEditIdx(i);
    setDialogMode("edit");
    setDialogError(false);
    setDialogOpen(true);
  };
  const openView = (i) => {
    setCurrentAnswer(answers[i]);
    setEditIdx(i);
    setDialogMode("view");
    setDialogError(false);
    setDialogOpen(true);
  };
  const save = () => {
    if (!currentAnswer.trim()) {
      setDialogError(true);
      return;
    }
    const arr = [...answers];
    editIdx === null ? arr.push(currentAnswer) : (arr[editIdx] = currentAnswer);
    setAnswers(arr);
    setDialogOpen(false);
  };
  const del = (i) => setAnswers(answers.filter((_, idx) => idx !== i));
  const isViewing = dialogMode === "view";

  // ---------------- render --------------------------------------------------
  return (
    <div className="annotation-form-field-container">
      {/* Standardized label row */}
      <div className="annotation-form-field-label">
        <span className={`label-text ${mandatory ? 'mandatory' : ''}`}>
          {label}
        </span>
        {mandatory && <span className="required-asterisk">*</span>}
        {!readOnly && allowMultipleAnswers && (
          <Tooltip title="Add answer">
            <IconButton
              onClick={openAdd}
              sx={{
                ml: 1,
                bgcolor: "common.black",
                color: "common.white",
                width: 26,
                height: 26,
                "&:hover": { bgcolor: "common.black" },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {instruction && <InfoTooltip instruction={instruction} />}
      </div>

      <Stack spacing={1} className="annotation-form-field-input">

      {/* multi‑answer blocks */}
      {allowMultipleAnswers && (() => {
        const filteredAnswers = answers.filter(ans => ans.trim());
        const useCompactLayout = shouldUseCompactLayout(filteredAnswers);
        
        if (useCompactLayout) {
          // Compact grid layout for short answers - wrapped in container box
          return (
            <Paper
              elevation={0}
              sx={{
                bgcolor: (t) => t.palette.grey[200],
                borderRadius: 2,
                p: 2,
                wordBreak: "break-word",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                {filteredAnswers.map((ans, idx) => (
                  <Paper
                    key={idx}
                    elevation={0}
                    onMouseEnter={() => setHover({ [idx]: true })}
                    onMouseLeave={() => setHover({ [idx]: false })}
                    sx={{
                      position: "relative",
                      bgcolor: (t) => t.palette.grey[300],
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      minHeight: "32px",
                      maxWidth: "fit-content",
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: "0.875rem",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "180px",
                      }}
                    >
                      {ans}
                    </Typography>

                    {readOnly ? (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          display: "flex",
                          visibility: hover[idx] ? "visible" : "hidden",
                          bgcolor: "white",
                          borderRadius: "50%",
                          boxShadow: 1,
                        }}
                      >
                        <Tooltip title="View full answer / 查看完整回答">
                          <IconButton
                            aria-label="View full answer / 查看完整回答"
                            size="small"
                            onClick={() => openView(idx)}
                            sx={{ width: 20, height: 20, p: 0.25 }}
                          >
                            <VisibilityIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          display: "flex",
                          gap: 0.25,
                          visibility: hover[idx] ? "visible" : "hidden",
                          bgcolor: "white",
                          borderRadius: "50%",
                          boxShadow: 1,
                        }}
                      >
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => openEdit(idx)}
                            sx={{ width: 20, height: 20, p: 0.25 }}
                          >
                            <EditIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            onClick={() => del(idx)}
                            sx={{ width: 20, height: 20, p: 0.25 }}
                          >
                            <CloseIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </Paper>
          );
        } else {
          // Vertical stack layout for long answers
          return filteredAnswers.map((ans, idx) => (
            <Paper
              key={idx}
              elevation={0}
              onMouseEnter={() => setHover({ [idx]: true })}
              onMouseLeave={() => setHover({ [idx]: false })}
              sx={{
                position: "relative",
                bgcolor: (t) => t.palette.grey[200],
                borderRadius: 2,
                p: 3,
                wordBreak: "break-word",
              }}
            >
              <Typography>{truncateAnswer(ans)}</Typography>

              {readOnly ? (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    display: "flex",
                    visibility: hover[idx] ? "visible" : "hidden",
                  }}
                >
                  <Tooltip title="View full answer / 查看完整回答">
                    <IconButton
                      aria-label="View full answer / 查看完整回答"
                      size="small"
                      onClick={() => openView(idx)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    display: "flex",
                    gap: 0.5,
                    visibility: hover[idx] ? "visible" : "hidden",
                  }}
                >
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(idx)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => del(idx)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Paper>
          ));
        }
      })()}

      {/* Empty state placeholder for multi-answer fields */}
      {allowMultipleAnswers && answers.filter(ans => ans.trim()).length === 0 && (
        <Paper
          elevation={0}
          sx={{
            bgcolor: (t) => t.palette.grey[200],
            borderRadius: 2,
            p: label.includes("Artist (歌手)") ? 1 : 3,
            wordBreak: "break-word",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: label.includes("Artist (歌手)") ? "40px" : "auto",
            minHeight: label.includes("Artist (歌手)") ? "40px" : "60px",
          }}
        >
          <Typography 
            sx={{ 
              color: (t) => t.palette.grey[600],
              fontStyle: "italic",
              textAlign: "center",
              fontSize: label.includes("Artist (歌手)") ? "0.875rem" : "1rem",
            }}
          >
            
          </Typography>
        </Paper>
      )}

      {/* single‑answer compact field */}
      {!allowMultipleAnswers && (
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          value={answers[0] ?? ""}
          disabled={readOnly}
          sx={{ wordBreak: "break-word", }}
          onChange={(e) => {
            const v = e.target.value;
            setAnswers(v ? [v] : []);
          }}
          onBlur={() =>
            setError(mandatory && !(answers[0] && answers[0].trim()))
          }
          error={showError && error}
          helperText={showError && error ? `${label} is required.` : ""}
        />
      )}

      {error && allowMultipleAnswers && showError && (
        <Typography color="error" variant="caption">
          {label} is required (enter at least one answer).
        </Typography>
      )}

      {/* dialog editor */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ backdrop: { sx: { bgcolor: "rgba(0,0,0,0.6)" } } }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center", py: 2 }}>
          {isViewing
            ? "View Answer / 查看回答"
            : editIdx === null
              ? "Add New Answer"
              : "Edit Answer"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 0 }}>
          {isViewing ? (
            <Box
              data-testid="free-response-view-content"
              sx={{
                bgcolor: (theme) => theme.palette.grey[100],
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                minHeight: 180,
                p: 2.5,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                userSelect: "text",
              }}
            >
              <Typography
                component="div"
                sx={{ whiteSpace: "inherit", overflowWrap: "inherit" }}
              >
                {currentAnswer}
              </Typography>
            </Box>
          ) : (
            <TextField
              value={currentAnswer}
              onChange={(e) => {
                setCurrentAnswer(e.target.value);
                setDialogError(!e.target.value.trim());
              }}
              multiline
              minRows={8}
              fullWidth
              placeholder="Enter your answer here…"
              error={dialogError}
              helperText={dialogError ? "Answer cannot be empty." : ""}
              sx={{
                bgcolor: (t) => t.palette.grey[200],
                borderRadius: 2,
                "& .MuiInputBase-input": { p: 2 },
              }}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 3 }}>
          {isViewing ? (
            <Button onClick={() => setDialogOpen(false)}>Close (关闭)</Button>
          ) : (
            <>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={save}
                disabled={!currentAnswer.trim()}
                sx={{
                  bgcolor: "common.black",
                  "&:hover": { bgcolor: "common.black" },
                }}
              >
                {editIdx === null ? "Add" : "Update"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      </Stack>
    </div>
  );
};

export default FreeResponse;
